import React from "react";
import { useTranslation } from "react-i18next";
import { SubPage, FullDivider } from "./SubPage";
import { navigate } from "../App";

const META: Record<string, { logo?: string; invertLogo?: boolean; lang: string; license: string }> = {
  cate: { logo: "/assets/cate-logo.svg", lang: "TypeScript", license: "MIT" },
  "0-ai": { lang: "TypeScript", license: "MIT" },
};

export function OpenSource() {
  const { t } = useTranslation("opensource");
  const repos = t("repos", { returnObjects: true }) as { name: string; slug: string | null; description: string }[];

  return (
    <SubPage title={t("title")} subtitle={t("subtitle")}>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-grey-800">
        <div className="bg-grey-975 px-5 sm:px-8 md:px-[66px] py-10 md:py-12">
          <h3 className="text-base font-semibold text-white mb-3">{t("philosophyHeading")}</h3>
          <p className="text-sm text-grey-400 leading-relaxed">{t("philosophyBody")}</p>
        </div>
        <div className="bg-grey-975 px-5 sm:px-8 md:px-[66px] py-10 md:py-12">
          <h3 className="text-base font-semibold text-white mb-3">{t("contributeHeading")}</h3>
          <p className="text-sm text-grey-400 leading-relaxed">{t("contributeBody")}</p>
          <a
            href="https://github.com/0-AI-UG"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-white/50 hover:text-white transition-colors mt-3"
          >
            {t("viewAllRepos")}
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="opacity-50">
              <path d="M1 11L11 1M11 1H4M11 1V8" stroke="currentColor" strokeWidth="1.2" />
            </svg>
          </a>
        </div>
      </div>
      <FullDivider />

      <div className="relative">
        <div className="absolute inset-0 dot-grid-sm dot-fade-b pointer-events-none" style={{ opacity: 0.15 }} />
        <div className="px-5 sm:px-8 md:px-[66px] pt-12 md:pt-16 pb-4 relative">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">{t("projectsHeading")}</h2>
        </div>
        <FullDivider />
        <div className="grid grid-cols-1 gap-px bg-grey-800 border-t border-grey-800 relative">
          {repos.map((repo) => {
            const meta = META[repo.slug ?? "0-ai"] ?? { lang: "TypeScript", license: "MIT" };
            return (
              <div key={repo.name} className="bg-grey-975 px-5 sm:px-8 md:px-[66px] py-8 md:py-10">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {meta.logo && repo.slug ? (
                      <a
                        href={`/products/${repo.slug}`}
                        onClick={(e) => { e.preventDefault(); navigate(`/products/${repo.slug}`); }}
                        className="hover:opacity-80 transition-opacity"
                      >
                        <img src={meta.logo} alt={repo.name} className={`h-4 w-auto object-contain${meta.invertLogo ? " invert" : ""}`} />
                      </a>
                    ) : repo.slug ? (
                      <a
                        href={`/products/${repo.slug}`}
                        onClick={(e) => { e.preventDefault(); navigate(`/products/${repo.slug}`); }}
                        className="text-sm font-semibold text-white/80 hover:text-white transition-colors"
                      >
                        {repo.name}
                      </a>
                    ) : (
                      <span className="text-sm font-semibold text-white/80">{repo.name}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[9px] text-white/20 font-mono">{meta.lang}</span>
                    <span className="text-[9px] text-white/20">{meta.license}</span>
                  </div>
                </div>
                <p className="text-xs text-white/35 leading-relaxed">{repo.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      <FullDivider />

    </SubPage>
  );
}
